import { ColTypeDef, SetFilterValuesFuncParams } from 'ag-grid-community';
import { ISearchResponse, ServerSideBlotterSearchRequest } from 'apiConnector/blotters';
import { ExternalFilter, useTranslateTradesRequest } from 'components/Blotters/Trades/Grid/utils';
import type { ColumnGroupPreferenceState, ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { useBlotterColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useBlotterColumnTypes';
import { useBlotterGridApi } from 'components/shared/Blotter/Grid/Hooks/useBlotterGridApi';
import { useBlotterServerSideDatasource } from 'components/shared/Blotter/Grid/Hooks/useBlotterServerSideDatasource';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useTranslateFilterInColumnDefinitions } from 'components/shared/Blotter/Grid/Hooks/useTranslateFilterInColumnDefinitions';
import { IntlKey } from 'locales';
import type { Observable } from 'rxjs';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import {
  TradesBlottersItemByBlotterType,
  TradesOrProducts,
  TradesOrProductsByBlotterType,
} from 'state/blotters/trades/types/blotterItem';

type Props<T extends TradesOrProducts, TRequest> = {
  // From Redux state
  columnDefinitions: readonly IBlotterColumnDefinition<TradesOrProductsByBlotterType<T>>[];
  blotterRequest: TRequest;
  columnState?: readonly ColumnPreferenceState[];
  columnGroupState?: readonly ColumnGroupPreferenceState[];
  pageSizeState?: number;
  isBlotterRefreshable: boolean;
  // Redux callbacks
  onBlotterColumnStateChanged: (
    columnState: readonly ColumnPreferenceState[],
    columnGroupState?: readonly ColumnGroupPreferenceState[],
  ) => void;
  onItemsReceived: (response: ISearchResponse<TradesBlottersItemByBlotterType<T>>) => void;
  onItemsRequested: () => void;
  onPageSizeChanged: (pageSize: number) => void;
  onRequestFailed: (error: Error) => void;
  // API & configuration
  searchItems: (req: ServerSideBlotterSearchRequest) => Observable<ISearchResponse<TradesBlottersItemByBlotterType<T>>>;
  setLastBlotterRequest: (value: ServerSideBlotterSearchRequest) => void;
  getFilterValues: (params: SetFilterValuesFuncParams) => void;
  getHeaderIntlKey: (key: string) => IntlKey;
  additionalColumns?: Record<string, ColTypeDef<TradesOrProductsByBlotterType<T>>>;
};

export function useTradeBlotterServerSideGridOptions<T extends TradesOrProducts, TRequest = ExternalFilter>({
  columnDefinitions,
  blotterRequest,
  columnState,
  columnGroupState,
  pageSizeState,
  isBlotterRefreshable,
  onBlotterColumnStateChanged,
  onItemsReceived,
  onItemsRequested,
  onRequestFailed,
  onPageSizeChanged,
  setLastBlotterRequest,
  searchItems,
  getFilterValues,
  getHeaderIntlKey,
  additionalColumns,
}: Props<T, TRequest>) {
  type TItem = TradesOrProductsByBlotterType<T>;

  const { onGridReady, getContextMenuItems } = useBlotterGridApi({
    logAnalyticName: 'Blotter My Trades',
    isRefreshable: isBlotterRefreshable,
  });

  const columnTypes = useBlotterColumnTypes<TItem>({ additionalColumns, precision: 6 });

  const defaultColDef = useDefaultColumnDefinition<TItem>(getHeaderIntlKey);

  const columnsDefinitionsWithTranslatedFilter = useTranslateFilterInColumnDefinitions(
    columnDefinitions,
    getFilterValues,
  );

  const translateRequest = useTranslateTradesRequest(columnDefinitions);

  const serverSideDatasource = useBlotterServerSideDatasource<TRequest, TradesBlottersItemByBlotterType<T>, Error>({
    blotterRequest,
    columnDefinitions: columnDefinitions as unknown as readonly IBlotterColumnDefinition<
      TradesBlottersItemByBlotterType<T>
    >[],
    searchItems,
    setLastBlotterRequest,
    translateRequest: translateRequest as any,
    onItemsRequested,
    onItemsReceived,
    onRequestFailed,
  });

  return {
    pageSizeState,
    columnGroupState,
    serverSideDatasource,
    columnDefinitions: columnsDefinitionsWithTranslatedFilter,
    columnTypes,
    defaultColDef,
    getContextMenuItems,
    onGridReady,
    onPageSizeChanged,
    columnState,
    onBlotterColumnStateChanged,
  };
}
